
<template>
  <div class="not-found-pages">
    <HeaderAdmin />
    <div class="dashboard-shell workspace-stack admin-shell" :class="{ 'admin-shell--swap': section === 'swap' }">
      <section v-if="section !== 'swap'" class="glass-surface workspace-hero workspace-hero--event admin-hero">
        <div>
          <span class="workspace-kicker">Admin FOMO</span>
          <h1 class="dashboard-title">Admin más claro para pagos, ledger y reportes</h1>
          <p class="dashboard-subtitle">Ahora el admin separa mejor la operación legacy, el ledger privado, los eventos editables y un reporte con rango de fechas y lectura mensual.</p>
        </div>
        <div class="admin-hero-stats">
          <article class="admin-stat"><small>Eventos</small><strong>{{ events.length }}</strong></article>
          <article class="admin-stat"><small>Ventas</small><strong>{{ aggregateMoney(heroSalesTotal, allEventCurrencies) }}</strong></article>
          <article class="admin-stat"><small>FOMO bruto</small><strong>{{ aggregateMoney(heroFomoGross, allEventCurrencies) }}</strong></article>
          <article class="admin-stat"><small>Pendiente org.</small><strong>{{ aggregateMoney(heroPendingTotal, allEventCurrencies) }}</strong></article>
        </div>
      </section>

      <label v-if="section === 'swap'" class="admin-swap-mobile-nav">Administración
        <select v-model="section" aria-label="Sección de administración"><option v-for="item in sections" :key="item.key" :value="item.key">{{ item.title }}</option></select>
      </label>
      <section class="admin-nav" aria-label="Administración">
        <button v-for="item in sections" :key="item.key" :class="['admin-nav__card', { 'is-active': section === item.key }]" type="button" @click="section = item.key">
          <i :class="item.icon"></i>
          <div>
            <strong>{{ item.title }}</strong>
            <small>{{ item.copy }}</small>
          </div>
          <span>{{ item.meta }}</span>
        </button>
      </section>

      <section v-if="section === 'review'" class="workspace-stack">
        <PaymentFeeConfigList v-if="!showLoading" style="padding-top: 20px; padding-bottom: 20px;" />
      </section>

      <section v-else-if="section === 'legacy'" class="workspace-stack">
        <article class="glass-surface workspace-panel">
          <div class="workspace-panel__header">
            <div>
              <span class="workspace-kicker">Legacy</span>
              <h2 class="workspace-title">Pagos heredados mejor agrupados</h2>
              <p class="workspace-copy">Lo viejo vive aquí en tres botones claros: aprobaciones, ordenes sin pago y revisión de fees.</p>
            </div>
          </div>
          <div class="admin-subnav">
            <button :class="['workspace-tab', { 'is-active': legacySection === 'approvals' }]" type="button" @click="legacySection = 'approvals'">Aprobar pagos</button>
            <button :class="['workspace-tab', { 'is-active': legacySection === 'manuals' }]" type="button" @click="legacySection = 'manuals'">Ordenes sin pago</button>
            <button :class="['workspace-tab', { 'is-active': legacySection === 'fees' }]" type="button" @click="legacySection = 'fees'">Revision y fees</button>
          </div>
        </article>
        <AssistanceList v-show="legacySection === 'approvals'" v-if="!showLoading" style="padding-top: 20px; padding-bottom: 20px;" />
        <PendingPaymentList v-show="legacySection === 'manuals'" v-if="!showLoading" style="padding-top: 20px; padding-bottom: 20px;" />
        <PaymentFeeConfigList v-show="legacySection === 'fees'" v-if="!showLoading" style="padding-top: 20px; padding-bottom: 20px;" />
      </section>

      <section v-else-if="section === 'ledger'" class="workspace-stack">
        <article class="glass-surface workspace-panel">
          <div class="workspace-panel__header">
            <div>
              <span class="workspace-kicker">Ledger privado</span>
              <h2 class="workspace-title">Que entra, que gana FOMO, que cuesta banco y que falta</h2>
              <p class="workspace-copy">Arranca con todo el historico por defecto. Si quieres, luego lo acotas por fechas, estado o busqueda.</p>
            </div>
          </div>

          <div class="admin-filters admin-filters--ledger">
            <div class="admin-search-cluster">
              <label class="workspace-search admin-filters__search"><i class="fas fa-search"></i><input v-model="ledgerSearchDraft" type="text" placeholder="Buscar evento, slug o venue" @keyup.enter="applyLedgerSearch" /></label>
              <button class="workspace-button workspace-button--primary admin-search-button" type="button" @click="applyLedgerSearch">
                <i class="fas fa-search"></i>
                <span>Buscar</span>
              </button>
            </div>
            <input v-model="ledgerDateFrom" class="workspace-input" type="date" />
            <input v-model="ledgerDateTo" class="workspace-input" type="date" />
            <select v-model="ledgerStatusFilter" class="workspace-input workspace-select"><option value="all">Todos</option><option value="reconciled">Reconciliados</option><option value="pending">Pendientes</option></select>
          </div>

          <div class="admin-presets">
            <button class="workspace-button workspace-button--small" type="button" @click="ledgerPreset('month')">Mes actual</button>
            <button class="workspace-button workspace-button--small" type="button" @click="ledgerPreset('quarter')">90 dias</button>
            <button class="workspace-button workspace-button--small" type="button" @click="ledgerPreset('ytd')">Ano actual</button>
            <button class="workspace-button workspace-button--small" type="button" @click="ledgerPreset('all')">Todo</button>
            <button class="workspace-button workspace-button--small" type="button" @click="clearLedgerSearch">Limpiar busqueda</button>
          </div>

          <div v-if="ledgerCurrencies.length" class="admin-subnav admin-subnav--currencies">
            <button v-for="currency in ledgerCurrencies" :key="currency" :class="['workspace-tab', { 'is-active': activeLedgerCurrency === currency }]" type="button" @click="ledgerCurrency = currency">{{ currency }}</button>
          </div>

          <div class="admin-kpis">
            <article v-for="card in ledgerCards" :key="card.label" class="admin-kpi admin-kpi--dual">
              <small>{{ card.label }}</small>
              <strong>{{ formatMoney(card.value, activeLedgerCurrency) }}</strong>
              <span v-if="showUsdEquivalent(activeLedgerCurrency)" class="admin-kpi__subvalue">USD eq. {{ formatMoney(card.usdValue, 'USD') }}</span>
              <p>{{ card.copy }}</p>
            </article>
          </div>

          <div v-if="loadingEvents" class="workspace-empty">Cargando ledger...</div>
          <div v-else-if="eventsError" class="workspace-feedback workspace-feedback--error">{{ eventsError }}</div>
          <div v-else-if="!ledgerEvents.length" class="workspace-empty">No hay eventos para ese filtro. Prueba Todo, cambia las fechas o usa la busqueda.</div>
          <div v-else class="workspace-table-shell">
            <table class="workspace-table admin-table admin-table--ledger">
              <thead><tr><th>Evento</th><th>Fecha</th><th>Organizer</th><th>FOMO</th><th>Banco y ops</th><th>Settlement</th><th>Estado</th></tr></thead>
              <tbody>
                <tr v-for="event in ledgerEvents" :key="event.id">
                  <td>
                    <div class="admin-name admin-name--stacked">
                      <strong>{{ event.name }}</strong>
                      <small>{{ event.slug || 'sin-slug' }}</small>
                      <div class="admin-inline-tags">
                        <span class="workspace-badge">{{ event.country_code || 'N/A' }}</span>
                        <span class="workspace-badge">{{ eventCurrency(event) }}</span>
                        <span v-if="venueName(event)" class="workspace-badge">{{ venueName(event) }}</span>
                        <span v-if="event.ledger_currency_mismatch" class="workspace-badge workspace-badge--warning">revisar moneda</span>
                      </div>
                      <div class="workspace-actions workspace-actions--compact workspace-actions--card admin-row-actions">
                        <button class="workspace-button workspace-button--primary workspace-button--small" type="button" @click="openEvent(event, 'finanzas')">Finanzas</button>
                        <button class="workspace-button workspace-button--small" type="button" @click="openEvent(event)">Editar</button>
                      </div>
                    </div>
                  </td>
                  <td>{{ formatDate(event.start_date) }}</td>
                  <td>
                    <div class="admin-money-stack">
                      <strong>{{ formatMoney(orgSales(event), eventCurrency(event)) }}</strong>
                      <small v-if="showUsdEquivalent(eventCurrency(event))">USD eq. {{ formatMoney(orgSalesUsd(event), 'USD') }}</small>
                      <span>Payable bruto organizer</span>
                    </div>
                  </td>
                  <td>
                    <div class="admin-money-stack">
                      <strong>{{ formatMoney(fomoRevenue(event), eventCurrency(event)) }}</strong>
                      <small v-if="showUsdEquivalent(eventCurrency(event))">USD eq. {{ formatMoney(fomoRevenueUsd(event), 'USD') }}</small>
                      <span>Neto {{ formatMoney(fomoNet(event), eventCurrency(event)) }}</span>
                      <small v-if="showUsdEquivalent(eventCurrency(event))">USD eq. {{ formatMoney(fomoNetUsd(event), 'USD') }}</small>
                    </div>
                  </td>
                  <td>
                    <div class="admin-money-stack">
                      <strong>{{ formatMoney(bankFee(event), eventCurrency(event)) }}</strong>
                      <small v-if="showUsdEquivalent(eventCurrency(event))">USD eq. {{ formatMoney(bankFeeUsd(event), 'USD') }}</small>
                      <span>{{ event.ledger_posted_payments || 0 }} posteados / {{ event.ledger_approved_payments || 0 }} aprobados</span>
                    </div>
                  </td>
                  <td>
                    <div class="admin-money-stack">
                      <strong>Pend. {{ formatMoney(orgPending(event), eventCurrency(event)) }}</strong>
                      <small v-if="showUsdEquivalent(eventCurrency(event))">USD eq. {{ formatMoney(orgPendingUsd(event), 'USD') }}</small>
                      <span>Dep. {{ formatMoney(orgDeposited(event), eventCurrency(event)) }}</span>
                      <small v-if="showUsdEquivalent(eventCurrency(event))">USD eq. {{ formatMoney(orgDepositedUsd(event), 'USD') }}</small>
                    </div>
                  </td>
                  <td>
                    <div class="admin-money-stack">
                      <span :class="['workspace-status-pill', event.ledger_is_reconciled ? 'is-active' : 'is-warning']">{{ event.ledger_is_reconciled ? 'Reconciliado' : 'Por revisar' }}</span>
                      <span>{{ event.ledger_missing_posted_payments || 0 }} faltantes</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section v-else-if="section === 'payouts'" class="workspace-stack">
        <PayoutOpsView v-if="!showLoading" />
      </section>

      <section v-else-if="section === 'connect'" class="workspace-stack">
        <StripeConnectOpsView v-if="!showLoading" />
      </section>

      <section v-else-if="section === 'swap'" class="workspace-stack">
        <AdminSwapOpsView v-if="!showLoading" />
      </section>

      <section v-else-if="section === 'events'" class="workspace-stack">
        <article class="glass-surface workspace-panel">
          <div class="workspace-panel__header">
            <div>
              <span class="workspace-kicker">Eventos</span>
              <h2 class="workspace-title">Directorio simple para editar y abrir checkout</h2>
              <p class="workspace-copy">Filtro por nombre, fecha del evento y estado temporal. Cada card abre edicion, finanzas o checkout.</p>
            </div>
          </div>

          <div class="admin-filters">
               <label class="workspace-search admin-filters__search"><i class="fas fa-search"></i><input v-model="eventSearch" type="text" placeholder="Buscar evento o descripción" /></label>
            <input v-model="eventDateFrom" class="workspace-input" type="date" />
            <input v-model="eventDateTo" class="workspace-input" type="date" />
            <select v-model="eventPhase" class="workspace-input workspace-select"><option value="all">Todos</option><option value="next">Proximos</option><option value="prev">Anteriores</option></select>
          </div>

          <div class="admin-kpis admin-kpis--four">
            <article class="admin-kpi"><small>Eventos</small><strong>{{ filteredEvents.length }}</strong><p>Dentro del filtro actual.</p></article>
            <article class="admin-kpi"><small>Proximos</small><strong>{{ upcomingEvents }}</strong><p>Con fecha futura o activa.</p></article>
            <article class="admin-kpi"><small>Ventas</small><strong>{{ aggregateMoney(filteredEventSales, filteredEventCurrencies) }}</strong><p>Venta acumulada filtrada.</p></article>
             <article class="admin-kpi"><small>FOMO bruto</small><strong>{{ aggregateMoney(filteredEventFomo, filteredEventCurrencies) }}</strong><p>Fee plataforma más Fomo Safe.</p></article>
          </div>

          <div v-if="loadingEvents" class="workspace-empty">Cargando eventos...</div>
          <div v-else-if="eventsError" class="workspace-feedback workspace-feedback--error">{{ eventsError }}</div>
          <div v-else-if="!filteredEvents.length" class="workspace-empty">No hay eventos para ese filtro.</div>
          <div v-else class="admin-grid">
            <article v-for="event in filteredEvents" :key="event.id" class="admin-card">
              <div class="workspace-badge-row workspace-badge-row--compact">
                <span class="workspace-badge">{{ bucket(event) === 'next' ? 'Proximo' : 'Anterior' }}</span>
                <span :class="['workspace-status-pill', reviewClass(event.review_status)]">{{ reviewLabel(event.review_status) }}</span>
                <span class="workspace-badge">{{ formatDate(event.start_date) }}</span>
              </div>
              <h3>{{ event.name }}</h3>
              <p>{{ event.description || 'Sin descripción cargada.' }}</p>
              <div class="admin-meta">
                <span><i class="fas fa-link"></i>{{ event.slug || 'sin-slug' }}</span>
                <span><i class="fas fa-ticket-alt"></i>{{ event.total_ticket_count || 0 }} vendidos</span>
                <span><i class="fas fa-chart-line"></i>{{ formatMoney(event.total_ticket_sales, eventCurrency(event)) }}</span>
              </div>
              <div class="workspace-actions workspace-actions--compact workspace-actions--card">
                <button class="workspace-button workspace-button--primary workspace-button--small" type="button" @click="openEvent(event)">Editar</button>
                <button class="workspace-button workspace-button--small" type="button" @click="openEvent(event, 'finanzas')">Finanzas</button>
                <button class="workspace-button workspace-button--small" type="button" @click="openReview(event)">Revision</button>
                <button class="workspace-button workspace-button--small" type="button" @click="openCheckout(event)">Checkout</button>
              </div>
            </article>
          </div>
        </article>
      </section>

      <section v-else class="workspace-stack">
        <article class="glass-surface workspace-panel">
          <div class="workspace-panel__header">
            <div>
              <span class="workspace-kicker">Reportes</span>
              <h2 class="workspace-title">Reporte personalizado por fechas</h2>
              <p class="workspace-copy">Este bloque usa fecha real de pago aprobado. Aquí ves ventas, fee banco, ingreso FOMO, neto FOMO y un gráfico mensual del rango elegido.</p>
            </div>
          </div>

          <div class="admin-report-filters">
            <input v-model="reportDateFrom" class="workspace-input" type="date" />
            <input v-model="reportDateTo" class="workspace-input" type="date" />
            <div class="admin-presets">
              <button class="workspace-button workspace-button--small" type="button" @click="preset('30d')">30 dias</button>
              <button class="workspace-button workspace-button--small" type="button" @click="preset('90d')">90 dias</button>
              <button class="workspace-button workspace-button--small" type="button" @click="preset('ytd')">Ano actual</button>
              <button class="workspace-button workspace-button--small" type="button" @click="preset('all')">Todo</button>
            </div>
            <button class="workspace-button workspace-button--primary" type="button" :disabled="analyticsLoading" @click="refreshReport">{{ analyticsLoading ? 'Actualizando...' : 'Actualizar reporte' }}</button>
          </div>

          <div v-if="reportCurrencies.length" class="admin-subnav admin-subnav--currencies">
            <button v-for="currency in reportCurrencies" :key="currency" :class="['workspace-tab', { 'is-active': reportCurrency === currency }]" type="button" @click="setReportCurrency(currency)">{{ currency }}</button>
          </div>

          <div class="admin-kpis admin-kpis--four">
            <article v-for="card in reportCards" :key="card.label" class="admin-kpi admin-kpi--dual">
              <small>{{ card.label }}</small>
              <strong>{{ card.count ? card.value : formatMoney(card.value, reportCurrency) }}</strong>
              <span v-if="!card.count && showUsdEquivalent(reportCurrency)" class="admin-kpi__subvalue">USD eq. {{ formatMoney(card.usdValue, 'USD') }}</span>
              <p>{{ card.copy }}</p>
            </article>
          </div>

          <div v-if="analyticsError" class="workspace-feedback workspace-feedback--error">{{ analyticsError }}</div>
          <div v-else-if="analyticsLoading" class="workspace-empty">Cargando reporte...</div>
          <template v-else>
            <div class="admin-report-grid">
              <article class="admin-panel-card">
                <div class="workspace-panel__header"><div><span class="workspace-kicker">Grafico mensual</span><h3 class="workspace-title">Ventas vs neto FOMO</h3></div></div>
                <div v-if="!reportMonthly.length" class="workspace-empty">No hay pagos aprobados en ese rango.</div>
                <div v-else class="admin-chart">
                  <article v-for="bucket in reportMonthly" :key="bucket.month" class="admin-chart__item">
                    <div class="admin-chart__bars">
                      <span class="admin-chart__bar admin-chart__bar--sales" :style="{ height: chartHeight(bucket.gross_sales) }"></span>
                      <span class="admin-chart__bar admin-chart__bar--net" :style="{ height: chartHeight(bucket.fomo_net) }"></span>
                    </div>
                    <strong>{{ month(bucket.month) }}</strong>
                    <small>{{ formatMoney(bucket.gross_sales, bucket.currency || reportCurrency) }}</small>
                  </article>
                </div>
              </article>

              <article class="admin-panel-card">
                <div class="workspace-panel__header"><div><span class="workspace-kicker">Top eventos</span><h3 class="workspace-title">Mas movimiento en el rango</h3></div></div>
                <div v-if="!reportEvents.length" class="workspace-empty">Sin eventos con pagos aprobados.</div>
                <div v-else class="admin-top">
                  <article v-for="event in reportEvents.slice(0, 6)" :key="event.event_id" class="admin-top__item">
                    <div>
                      <strong>{{ event.name }}</strong>
                      <small>{{ formatDate(event.start_date) }}</small>
                      <div class="admin-inline-tags">
                        <span class="workspace-badge">{{ event.country_code || 'N/A' }}</span>
                        <span class="workspace-badge">{{ event.currency || reportCurrency }}</span>
                      </div>
                    </div>
                    <div class="admin-money-stack">
                      <strong>{{ formatMoney(event.gross_sales, event.currency || reportCurrency) }}</strong>
                      <small v-if="showUsdEquivalent(event.currency || reportCurrency)">USD eq. {{ formatMoney(reportUsd(event, 'gross_sales'), 'USD') }}</small>
                      <span>FOMO neto {{ formatMoney(event.fomo_net, event.currency || reportCurrency) }}</span>
                      <small v-if="showUsdEquivalent(event.currency || reportCurrency)">USD eq. {{ formatMoney(reportUsd(event, 'fomo_net'), 'USD') }}</small>
                    </div>
                    <div class="workspace-actions workspace-actions--compact">
                      <button class="workspace-button workspace-button--primary workspace-button--small" type="button" @click="openEvent(event)">Resumen</button>
                      <button class="workspace-button workspace-button--small" type="button" @click="openEvent(event, 'finanzas')">Finanzas</button>
                    </div>
                  </article>
                </div>
              </article>
            </div>

            <div class="admin-report-tables">
              <article v-if="reportMonthly.length" class="admin-panel-card">
                <div class="workspace-panel__header"><div><span class="workspace-kicker">Serie mensual</span><h3 class="workspace-title">Lectura limpia por mes</h3></div></div>
                <div class="workspace-table-shell">
                  <table class="workspace-table admin-table">
                    <thead><tr><th>Mes</th><th>Venta bruta</th><th>Ingreso FOMO</th><th>Fee banco</th><th>Neto FOMO</th><th>Ordenes</th></tr></thead>
                    <tbody>
                      <tr v-for="bucket in reportMonthly" :key="bucket.month">
                        <td>{{ month(bucket.month) }}</td>
                        <td><div class="admin-money-stack"><strong>{{ formatMoney(bucket.gross_sales, bucket.currency || reportCurrency) }}</strong><small v-if="showUsdEquivalent(bucket.currency || reportCurrency)">USD eq. {{ formatMoney(reportUsd(bucket, 'gross_sales'), 'USD') }}</small></div></td>
                        <td><div class="admin-money-stack"><strong>{{ formatMoney(bucket.fomo_revenue_gross, bucket.currency || reportCurrency) }}</strong><small v-if="showUsdEquivalent(bucket.currency || reportCurrency)">USD eq. {{ formatMoney(reportUsd(bucket, 'fomo_revenue_gross'), 'USD') }}</small></div></td>
                        <td><div class="admin-money-stack"><strong>{{ formatMoney(bucket.bank_fee_total, bucket.currency || reportCurrency) }}</strong><small v-if="showUsdEquivalent(bucket.currency || reportCurrency)">USD eq. {{ formatMoney(reportUsd(bucket, 'bank_fee_total'), 'USD') }}</small></div></td>
                        <td><div class="admin-money-stack"><strong>{{ formatMoney(bucket.fomo_net, bucket.currency || reportCurrency) }}</strong><small v-if="showUsdEquivalent(bucket.currency || reportCurrency)">USD eq. {{ formatMoney(reportUsd(bucket, 'fomo_net'), 'USD') }}</small></div></td>
                        <td>{{ bucket.orders_count }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </article>

              <article v-if="reportEvents.length" class="admin-panel-card">
                <div class="workspace-panel__header"><div><span class="workspace-kicker">Eventos del rango</span><h3 class="workspace-title">Pais, moneda, local y USD equivalente</h3></div></div>
                <div class="workspace-table-shell">
                  <table class="workspace-table admin-table">
                    <thead><tr><th>Evento</th><th>Venta bruta</th><th>FOMO neto</th><th>Organizer</th><th>Depositos</th><th>Ordenes</th></tr></thead>
                    <tbody>
                      <tr v-for="event in reportEvents" :key="event.event_id">
                        <td>
                          <div class="admin-name admin-name--stacked">
                            <strong>{{ event.name }}</strong>
                            <small>{{ event.slug || 'sin-slug' }} · {{ formatDate(event.start_date) }}</small>
                            <div class="admin-inline-tags">
                              <span class="workspace-badge">{{ event.country_code || 'N/A' }}</span>
                              <span class="workspace-badge">{{ event.currency || reportCurrency }}</span>
                            </div>
                          </div>
                        </td>
                        <td><div class="admin-money-stack"><strong>{{ formatMoney(event.gross_sales, event.currency || reportCurrency) }}</strong><small v-if="showUsdEquivalent(event.currency || reportCurrency)">USD eq. {{ formatMoney(reportUsd(event, 'gross_sales'), 'USD') }}</small></div></td>
                        <td><div class="admin-money-stack"><strong>{{ formatMoney(event.fomo_net, event.currency || reportCurrency) }}</strong><small v-if="showUsdEquivalent(event.currency || reportCurrency)">USD eq. {{ formatMoney(reportUsd(event, 'fomo_net'), 'USD') }}</small><span>Banco {{ formatMoney(event.bank_fee_total, event.currency || reportCurrency) }}</span></div></td>
                        <td><div class="admin-money-stack"><strong>{{ formatMoney(event.organizer_payable, event.currency || reportCurrency) }}</strong><small v-if="showUsdEquivalent(event.currency || reportCurrency)">USD eq. {{ formatMoney(reportUsd(event, 'organizer_payable'), 'USD') }}</small><span>Pend. {{ formatMoney(event.pending_estimated, event.currency || reportCurrency) }}</span><small v-if="showUsdEquivalent(event.currency || reportCurrency)">USD eq. {{ formatMoney(reportUsd(event, 'pending_estimated'), 'USD') }}</small></div></td>
                        <td><div class="admin-money-stack"><strong>{{ formatMoney(event.deposited_total, event.currency || reportCurrency) }}</strong><small v-if="showUsdEquivalent(event.currency || reportCurrency)">USD eq. {{ formatMoney(reportUsd(event, 'deposited_total'), 'USD') }}</small></div></td>
                        <td>{{ event.orders_count }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </article>
            </div>
          </template>
        </article>
      </section>
    </div>
  </div>
</template>

<script>
import { apiService } from '../../common/api.services.js'
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../common/currency.js'
import AssistanceList from './AssistanceList.vue'
import PaymentFeeConfigList from './PaymentFeeConfigList.vue'
import PendingPaymentList from './PendingPaymentList.vue'
import PayoutOpsView from './PayoutOpsView.vue'
import StripeConnectOpsView from './StripeConnectOpsView.vue'
import AdminSwapOpsView from './AdminSwapOpsView.vue'
import HeaderAdmin from '../../components/HeaderAdmin.vue'

export default {
  name: 'AdminView',
  components: { HeaderAdmin, AssistanceList, PaymentFeeConfigList, PendingPaymentList, PayoutOpsView, StripeConnectOpsView, AdminSwapOpsView },
  data() {
    const today = new Date()
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
    const formatLocalInputDate = (value) => {
      const year = value.getFullYear()
      const month = String(value.getMonth() + 1).padStart(2, '0')
      const day = String(value.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    return {
      section: 'legacy',
      legacySection: 'approvals',
      showLoading: false,
      events: [],
      loadingEvents: false,
      eventsError: '',
      ledgerSearch: '',
      ledgerSearchDraft: '',
      ledgerDateFrom: '',
      ledgerDateTo: '',
      ledgerStatusFilter: 'all',
      ledgerCurrency: '',
      eventSearch: '',
      eventDateFrom: '',
      eventDateTo: '',
      eventPhase: 'all',
      analyticsLoading: false,
      analyticsError: '',
      reportDateFrom: formatLocalInputDate(monthStart),
      reportDateTo: formatLocalInputDate(today),
      reportSummary: { gross_sales: 0, fomo_revenue_gross: 0, bank_fee_total: 0, fomo_net: 0, deposited_total: 0, orders_count: 0 },
      reportMonthly: [],
      reportEvents: [],
      reportCurrencyGroups: [],
      reportCurrency: '',
      reportLastUpdated: '',
    }
  },
  computed: {
    allEventCurrencies() { return [...new Set(this.events.map((event) => this.eventCurrency(event)).filter(Boolean))].sort() },
    heroSalesTotal() { return this.events.reduce((sum, event) => sum + this.num(event.total_ticket_sales), 0) },
    heroFomoGross() { return this.events.reduce((sum, event) => sum + this.fomoRevenue(event), 0) },
    heroPendingTotal() { return this.events.reduce((sum, event) => sum + this.orgPending(event), 0) },
    pendingReviewTotal() { return this.events.filter((event) => event.review_status === 'pending').length },
    sections() {
      return [
        { key: 'review', icon: 'fas fa-user-check', title: 'Revisión eventos', copy: 'Aprueba o rechaza eventos rápido.', meta: `${this.pendingReviewTotal}` },
        { key: 'legacy', icon: 'fas fa-cash-register', title: 'Pagos legacy', copy: 'Aprobaciones, ordenes sin pago y fees.', meta: '3 frentes' },
        { key: 'ledger', icon: 'fas fa-book', title: 'Ledger', copy: 'Contabilidad privada con numeros claros.', meta: `${this.ledgerEvents.length}` },
        { key: 'payouts', icon: 'fas fa-money-check-dollar', title: 'Payouts', copy: 'Orders, providers y buckets por rail.', meta: 'ops' },
        { key: 'connect', icon: 'fas fa-link', title: 'Stripe Connect', copy: 'Cuentas, onboarding y payouts API.', meta: 'cards' },
        { key: 'swap', icon: 'fas fa-right-left', title: 'Swap', copy: 'Reventa, vendedores y entregas.', meta: 'EC' },
        { key: 'events', icon: 'fas fa-calendar-alt', title: 'Eventos', copy: 'Editar, abrir finanzas y checkout.', meta: `${this.filteredEvents.length}` },
        { key: 'reports', icon: 'fas fa-chart-line', title: 'Reportes', copy: 'Rango custom y gráfico mensual.', meta: `${this.reportCurrencyGroups.length || this.reportMonthly.length}` },
      ]
    },
    ledgerEventPool() {
      const term = this.ledgerSearch.trim().toLowerCase()
      return this.events.filter((event) => {
        if (event.is_non_real_finance) return false
        if (!this.inRange(event, this.ledgerDateFrom, this.ledgerDateTo)) return false
        if (this.ledgerStatusFilter === 'reconciled' && !event.ledger_is_reconciled) return false
        if (this.ledgerStatusFilter === 'pending' && event.ledger_is_reconciled) return false
        if (!term) return true
        return `${event.name || ''} ${event.slug || ''} ${this.venueName(event)}`.toLowerCase().includes(term)
      }).slice().sort((a, b) => this.orgPending(b) - this.orgPending(a))
    },
    ledgerCurrencies() {
      return [...new Set(this.ledgerEventPool.map((event) => this.eventCurrency(event)).filter(Boolean))].sort()
    },
    activeLedgerCurrency() {
      return this.ledgerCurrencies.includes(this.ledgerCurrency)
        ? this.ledgerCurrency
        : (this.ledgerCurrencies[0] || 'USD')
    },
    ledgerEvents() {
      return this.ledgerEventPool.filter((event) => this.eventCurrency(event) === this.activeLedgerCurrency)
    },
    ledgerCards() {
      const orgSales = this.ledgerEvents.reduce((sum, event) => sum + this.orgSales(event), 0)
      const fomo = this.ledgerEvents.reduce((sum, event) => sum + this.fomoRevenue(event), 0)
      const bank = this.ledgerEvents.reduce((sum, event) => sum + this.bankFee(event), 0)
      const pending = this.ledgerEvents.reduce((sum, event) => sum + this.orgPending(event), 0)
      const deposited = this.ledgerEvents.reduce((sum, event) => sum + this.orgDeposited(event), 0)
      const orgSalesUsd = this.ledgerEvents.reduce((sum, event) => sum + this.orgSalesUsd(event), 0)
      const fomoUsd = this.ledgerEvents.reduce((sum, event) => sum + this.fomoRevenueUsd(event), 0)
      const bankUsd = this.ledgerEvents.reduce((sum, event) => sum + this.bankFeeUsd(event), 0)
      const pendingUsd = this.ledgerEvents.reduce((sum, event) => sum + this.orgPendingUsd(event), 0)
      const depositedUsd = this.ledgerEvents.reduce((sum, event) => sum + this.orgDepositedUsd(event), 0)
      return [
        { label: 'Ventas organizer', value: orgSales, usdValue: orgSalesUsd, copy: 'Caja generada para organizer.' },
        { label: 'Ingreso bruto FOMO', value: fomo, usdValue: fomoUsd, copy: 'Fee plataforma más Fomo Safe.' },
        { label: 'Fee banco', value: bank, usdValue: bankUsd, copy: 'Costo del procesador.' },
        { label: 'Neto FOMO', value: fomo - bank, usdValue: fomoUsd - bankUsd, copy: 'Lo que queda luego del banco.' },
        { label: 'Pendiente', value: pending, usdValue: pendingUsd, copy: 'Lo que aún falta depositar.' },
        { label: 'Depositado', value: deposited, usdValue: depositedUsd, copy: 'Lo ya liquidado al organizer.' },
      ]
    },
    filteredEvents() {
      const term = this.eventSearch.trim().toLowerCase()
      return this.events.filter((event) => {
        if (!this.inRange(event, this.eventDateFrom, this.eventDateTo)) return false
        if (this.eventPhase !== 'all' && this.bucket(event) !== this.eventPhase) return false
        if (!term) return true
        return `${event.name || ''} ${event.slug || ''} ${event.description || ''} ${this.venueName(event)}`.toLowerCase().includes(term)
      }).slice().sort((a, b) => (new Date(b.start_date || 0).getTime() || 0) - (new Date(a.start_date || 0).getTime() || 0))
    },
    upcomingEvents() { return this.filteredEvents.filter((event) => this.bucket(event) === 'next').length },
    filteredEventSales() { return this.filteredEvents.reduce((sum, event) => sum + this.num(event.total_ticket_sales), 0) },
    filteredEventFomo() { return this.filteredEvents.reduce((sum, event) => sum + this.fomoRevenue(event), 0) },
    filteredEventCurrencies() { return [...new Set(this.filteredEvents.map((event) => this.eventCurrency(event)).filter(Boolean))].sort() },
    reportCurrencies() {
      return this.reportCurrencyGroups.map((group) => group.currency)
    },
    reportCards() {
      const usd = this.reportSummary && this.reportSummary.usd_equivalent ? this.reportSummary.usd_equivalent : {}
      return [
        { label: 'Venta bruta', value: this.reportSummary.gross_sales, usdValue: this.num(usd.gross_sales), copy: 'Pagos aprobados del rango.' },
        { label: 'Ingreso bruto FOMO', value: this.reportSummary.fomo_revenue_gross, usdValue: this.num(usd.fomo_revenue_gross), copy: 'Fee plataforma más Fomo Safe.' },
        { label: 'Fee banco', value: this.reportSummary.bank_fee_total, usdValue: this.num(usd.bank_fee_total), copy: 'Costo bancario del rango.' },
        { label: 'Neto FOMO', value: this.reportSummary.fomo_net, usdValue: this.num(usd.fomo_net), copy: 'Margen de FOMO del rango.' },
        { label: 'Depósitos', value: this.reportSummary.deposited_total, usdValue: this.num(usd.deposited_total), copy: 'Settlements registrados.' },
        { label: 'Órdenes', value: this.reportSummary.orders_count, copy: 'Compras aprobadas.', count: true },
      ]
    },
    chartMax() {
      const values = this.reportMonthly.flatMap((item) => [this.num(item.gross_sales), this.num(item.fomo_net)])
      return values.length ? Math.max(...values, 1) : 1
    },
  },
  created() {
    this.syncTab()
    this.syncLegacy()
    this.loadEvents()
    this.loadReport()
  },
  methods: {
    num(value) { const parsed = Number.parseFloat(value); return Number.isNaN(parsed) ? 0 : parsed },
    money(value) { return this.num(value).toFixed(2) },
    normalizeCurrency(value) { return normalizeCurrencyCode(value, 'USD') },
    formatMoney(value, currency) { return formatCurrencyAmount(value, currency || 'USD') },
    showUsdEquivalent(currency) { return this.normalizeCurrency(currency || 'USD') !== 'USD' },
    eventCurrency(event) { return this.normalizeCurrency(event && (event.presentment_currency || event.currency)) },
    aggregateMoney(value, currencies) {
      if (!Array.isArray(currencies) || !currencies.length) return '--'
      return Array.isArray(currencies) && currencies.length === 1
        ? this.formatMoney(value, currencies[0])
        : 'Varias monedas'
    },
    parseLocalDate(value) {
      if (!value) return null
      if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
      if (typeof value === 'string') {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
        if (match) {
          const [, year, month, day] = match
          return new Date(Number(year), Number(month) - 1, Number(day))
        }
      }
      const parsed = new Date(value)
      return Number.isNaN(parsed.getTime()) ? null : parsed
    },
    dateKey(value) {
      if (!value) return ''
      if (typeof value === 'string') return value.slice(0, 10)
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    inRange(event, from, to) {
      const startKey = this.dateKey(event && (event.start_date || event.end_date))
      const endKey = this.dateKey(event && (event.end_date || event.start_date))
      const effectiveStart = startKey || endKey
      const effectiveEnd = endKey || startKey
      if (from && effectiveEnd && effectiveEnd < from) return false
      if (to && effectiveStart && effectiveStart > to) return false
      return true
    },
    bucket(event) {
      const date = new Date((event && (event.end_date || event.start_date)) || 0)
      return date.getTime() >= Date.now() ? 'next' : 'prev'
    },
    reviewLabel(value) {
      if (value === 'approved') return 'Aprobado'
      if (value === 'changes_requested') return 'Cambios solicitados'
      if (value === 'rejected') return 'Rechazado'
      return 'Pendiente'
    },
    reviewClass(value) {
      if (value === 'approved') return 'is-active'
      if (value === 'changes_requested') return 'is-warning'
      if (value === 'rejected') return 'is-danger'
      return 'is-warning'
    },
    formatDate(value) {
      const parsed = this.parseLocalDate(value)
      return parsed ? new Intl.DateTimeFormat('es-EC', { dateStyle: 'medium' }).format(parsed) : 'Sin fecha'
    },
    month(value) {
      const parsed = this.parseLocalDate(value)
      return parsed ? new Intl.DateTimeFormat('es-EC', { month: 'short', year: 'numeric' }).format(parsed) : '--'
    },
    orgSales(event) { return this.num(event && event.ledger_organizer_sales_gross) },
    fomoRevenue(event) { return this.num(event && event.ledger_platform_fee_gross) + this.num(event && event.ledger_fomo_safe_gross) },
    bankFee(event) { return this.num(event && event.ledger_processor_fee_total) },
    fomoNet(event) { return this.fomoRevenue(event) - this.bankFee(event) },
    orgPending(event) { return this.num(event && event.ledger_organizer_pending) },
    orgDeposited(event) { return this.num(event && event.ledger_organizer_deposited) },
    ledgerUsd(event, key) { return this.num(event && event.ledger_usd_equivalent && event.ledger_usd_equivalent[key]) },
    orgSalesUsd(event) { return this.ledgerUsd(event, 'organizer_sales_gross') },
    fomoRevenueUsd(event) { return this.ledgerUsd(event, 'fomo_revenue_gross') },
    bankFeeUsd(event) { return this.ledgerUsd(event, 'processor_fee_total') },
    fomoNetUsd(event) { return this.ledgerUsd(event, 'fomo_net') },
    orgPendingUsd(event) { return this.ledgerUsd(event, 'organizer_pending') },
    orgDepositedUsd(event) { return this.ledgerUsd(event, 'organizer_deposited') },
    reportUsd(record, key) { return this.num(record && record.usd_equivalent && record.usd_equivalent[key]) },
    venueName(event) { return event && event.venue && event.venue.name ? event.venue.name : '' },
    syncTab() {
      const tab = typeof this.$route.query.tab === 'string' ? this.$route.query.tab : 'legacy'
      this.section = ['review', 'legacy', 'ledger', 'payouts', 'connect', 'swap', 'events', 'reports'].includes(tab) ? tab : 'legacy'
    },
    syncLegacy() {
      const legacy = typeof this.$route.query.legacy === 'string' ? this.$route.query.legacy : this.$route.query.tab
      this.legacySection = legacy === 'facturas' ? 'manuals' : legacy === 'fees' ? 'fees' : 'approvals'
    },
    syncRoute() {
      const query = { ...this.$route.query, tab: this.section === 'legacy' ? 'legacy' : this.section }
      if (this.section === 'legacy') query.legacy = this.legacySection === 'manuals' ? 'facturas' : this.legacySection === 'fees' ? 'fees' : 'transferencias'
      else delete query.legacy
      if (this.section !== 'review') delete query.reviewSearch
      this.$router.replace({ name: 'AdminView', query })
    },
    formatInputDate(value) {
      const parsed = this.parseLocalDate(value)
      if (!parsed) return ''
      const year = parsed.getFullYear()
      const month = String(parsed.getMonth() + 1).padStart(2, '0')
      const day = String(parsed.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    loadEvents() {
      this.loadingEvents = true
      this.eventsError = ''
      apiService('/api/host/admin/events/catalog/').then((payload) => {
        if (payload && payload.err) {
          this.eventsError = payload.err || 'No pudimos cargar los eventos.'
          this.events = []
          return
        }
        const items = Array.isArray(payload) ? payload : []
        const map = new Map()
        items.forEach((event) => {
          if (event && event.id) {
            map.set(event.id, {
              ...event,
              presentment_currency: this.eventCurrency(event),
            })
          }
        })
        this.events = Array.from(map.values())
      }).finally(() => { this.loadingEvents = false })
    },
    applyLedgerSearch() {
      this.ledgerSearch = (this.ledgerSearchDraft || '').trim()
    },
    clearLedgerSearch() {
      this.ledgerSearchDraft = ''
      this.ledgerSearch = ''
    },
    ledgerPreset(type) {
      const today = new Date()
      if (type === 'all') {
        this.ledgerDateFrom = ''
        this.ledgerDateTo = ''
        return
      }

      if (type === 'ytd') {
        this.ledgerDateFrom = this.formatInputDate(new Date(today.getFullYear(), 0, 1))
        this.ledgerDateTo = this.formatInputDate(today)
        return
      }

      if (type === 'quarter') {
        const from = new Date(today)
        from.setDate(from.getDate() - 90)
        this.ledgerDateFrom = this.formatInputDate(from)
        this.ledgerDateTo = this.formatInputDate(today)
        return
      }

      this.ledgerDateFrom = this.formatInputDate(new Date(today.getFullYear(), today.getMonth(), 1))
      this.ledgerDateTo = this.formatInputDate(new Date(today.getFullYear(), today.getMonth() + 1, 0))
    },
    setReportCurrency(value) {
      const preferredCurrency = this.normalizeCurrency(value || this.reportCurrency)
      const activeGroup = this.reportCurrencyGroups.find((group) => group.currency === preferredCurrency)
        || this.reportCurrencyGroups[0]

      this.reportCurrency = activeGroup ? activeGroup.currency : this.normalizeCurrency(preferredCurrency)
      this.reportSummary = activeGroup && activeGroup.summary
        ? activeGroup.summary
        : { gross_sales: 0, fomo_revenue_gross: 0, bank_fee_total: 0, fomo_net: 0, deposited_total: 0, orders_count: 0 }
      this.reportMonthly = activeGroup && Array.isArray(activeGroup.monthly) ? activeGroup.monthly : []
      this.reportEvents = activeGroup && Array.isArray(activeGroup.events) ? activeGroup.events : []
    },
    loadReport() {
      this.analyticsLoading = true
      this.analyticsError = ''
      const query = new URLSearchParams()
      if (this.reportDateFrom) query.set('from', this.reportDateFrom)
      if (this.reportDateTo) query.set('to', this.reportDateTo)
      apiService(`/api/host/admin/finance-summary/${query.toString() ? `?${query.toString()}` : ''}`).then((data) => {
        if (data && data.err) {
          this.analyticsError = data.err
          this.reportSummary = { gross_sales: 0, fomo_revenue_gross: 0, bank_fee_total: 0, fomo_net: 0, deposited_total: 0, orders_count: 0 }
          this.reportMonthly = []
          this.reportEvents = []
          this.reportCurrencyGroups = []
          return
        }
        const currencyGroups = Array.isArray(data && data.currencies) && data.currencies.length
          ? data.currencies.map((group) => ({
            ...group,
            currency: this.normalizeCurrency(group.currency),
          }))
          : (data && data.summary && data.summary.currency
            ? [{
              currency: this.normalizeCurrency(data.summary.currency),
              summary: data.summary,
              monthly: Array.isArray(data.monthly) ? data.monthly : [],
              events: Array.isArray(data.events) ? data.events : [],
            }]
            : [])
        this.reportCurrencyGroups = currencyGroups
        this.setReportCurrency(this.reportCurrency)
        this.reportLastUpdated = new Date().toISOString()
      }).finally(() => { this.analyticsLoading = false })
    },
    refreshReport() {
      if (this.reportDateFrom && this.reportDateTo && this.reportDateFrom > this.reportDateTo) {
        this.analyticsError = 'La fecha inicial no puede ser mayor a la final.'
        return
      }
      this.loadReport()
    },
    preset(type) {
      const today = new Date()
      const fmt = (value) => this.formatInputDate(value)
      if (type === 'all') {
        this.reportDateFrom = ''
        this.reportDateTo = fmt(today)
      } else if (type === 'ytd') {
        this.reportDateFrom = fmt(new Date(today.getFullYear(), 0, 1))
        this.reportDateTo = fmt(today)
      } else {
        const from = new Date(today)
        from.setDate(from.getDate() - (type === '90d' ? 90 : 30))
        this.reportDateFrom = fmt(from)
        this.reportDateTo = fmt(today)
      }
      this.loadReport()
    },
    chartHeight(value) {
      const amount = this.num(value)
      if (amount <= 0) return '6px'
      return `${Math.max(8, (amount / this.chartMax) * 100)}%`
    },
    openCheckout(event) {
      if (event && event.slug) window.open(`${window.location.origin}/event/${event.slug}`, '_blank')
    },
    openReview(event) {
      const term = event && (event.slug || event.name || '')
      this.$router.push({ name: 'AdminView', query: { ...this.$route.query, tab: 'review', reviewSearch: term } })
    },
    openEvent(record, tab = 'resumen') {
      const id = record && (record.id || record.event_id)
      if (!id) return
      const query = tab !== 'resumen' ? { tab } : {}
      this.$router.push({ name: 'EventHostView', params: { slug: id }, query })
    },
  },
  watch: {
    '$route.query.tab'() { this.syncTab(); this.syncLegacy() },
    '$route.query.legacy'() { this.syncLegacy() },
    section() { this.syncRoute() },
    legacySection() { if (this.section === 'legacy') this.syncRoute() },
  },
}
</script>

<style>
.admin-swap-mobile-nav { display: none; }
.admin-shell--swap .admin-nav { display: flex; gap: 4px; flex-wrap: wrap; padding-bottom: 16px; border-bottom: 1px solid var(--fomo-border); }
.admin-shell--swap .admin-nav__card { display: flex; gap: 8px; min-height: 40px; padding: 8px 12px; border-radius: 10px; border: 1px solid transparent; background: transparent; color: var(--fomo-dashboard-copy); }
.admin-shell--swap .admin-nav__card strong { font: 500 12px/1.5 var(--fomo-font-button); }
.admin-shell--swap .admin-nav__card small, .admin-shell--swap .admin-nav__card > span { display: none; }
.admin-shell--swap .admin-nav__card.is-active { background: var(--fomo-control); border-color: var(--fomo-border); color: var(--fomo-dashboard-text); }
.admin-shell--swap .admin-nav__card:focus-visible { outline: 2px solid var(--fomo-dashboard-label); outline-offset: 3px; }
@media (max-width: 700px) {
  .admin-shell--swap .admin-nav { display: none; }
  .admin-swap-mobile-nav { display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--fomo-dashboard-copy); font: 12px/1.5 var(--fomo-font-body); }
  .admin-swap-mobile-nav select { min-height: 40px; padding: 8px 32px 8px 12px; border: 1px solid var(--fomo-border); border-radius: 10px; background: var(--fomo-control); color: var(--fomo-dashboard-text); font: 13px/1.5 var(--fomo-font-body); color-scheme: dark; }
}
.admin-shell { padding-bottom: 40px; }
.admin-hero { background: linear-gradient(135deg, rgba(10, 18, 40, 0.96), rgba(24, 40, 84, 0.94)); }
.admin-hero-stats, .admin-nav, .admin-kpis, .admin-grid, .admin-report-grid { display: grid; gap: 14px; }
.admin-hero-stats { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.admin-nav { grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
.admin-nav__card, .admin-stat, .admin-kpi, .admin-card, .admin-panel-card, .admin-top__item { padding: 16px; border-radius: 22px; background: rgba(255,255,255,0.04); border: 1px solid rgba(186,204,255,0.12); color: inherit; }
.admin-nav__card { display: grid; grid-template-columns: auto 1fr auto; gap: 12px; align-items: center; text-align: left; }
.admin-nav__card div, .admin-stat, .admin-kpi { display: grid; gap: 5px; align-content: start; }
.admin-stat small, .admin-stat strong, .admin-kpi small, .admin-kpi strong, .admin-kpi p, .admin-nav__card strong, .admin-nav__card small { display: block; }
.admin-nav__card.is-active { background: linear-gradient(145deg, rgba(44,101,255,0.18), rgba(19,29,59,0.92)); border-color: rgba(121,169,255,0.42); }
.admin-nav__card small, .admin-stat small, .admin-kpi small, .admin-name small, .admin-meta span, .admin-top__item small { color: rgba(208,219,238,0.74); }
.admin-nav__card span:last-child { padding: 6px 10px; border-radius: 999px; background: rgba(255,255,255,0.08); }
.workspace-status-pill.is-danger { background: rgba(168, 76, 76, 0.18); color: #ffd7d7; border-color: rgba(255, 154, 154, 0.24); }
.admin-subnav, .admin-presets { display: flex; flex-wrap: wrap; gap: 10px; }
.admin-filters, .admin-report-filters { display: grid; grid-template-columns: minmax(0,1.2fr) repeat(3, minmax(160px, 0.42fr)); gap: 12px; align-items: end; }
.admin-filters--ledger { grid-template-columns: minmax(0, 1.35fr) repeat(3, minmax(160px, 0.35fr)); }
.admin-filters__search { width: 100%; }
.admin-presets { margin-top: -2px; }
.admin-search-cluster { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 10px; align-items: center; }
.admin-search-button { min-height: 52px; }
.admin-kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.admin-kpis--four { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.admin-kpi--dual { align-content: start; }
.admin-kpi__subvalue { display: block; color: rgba(143, 235, 201, 0.9); font-size: 13px; }
.admin-kpi p, .admin-card p { margin: 0; color: rgba(224,232,246,0.82); }
.admin-name, .admin-meta, .admin-top { display: grid; gap: 6px; }
.admin-name--stacked { align-content: start; }
.admin-inline-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.workspace-badge--warning { background: rgba(255, 191, 118, 0.12); border: 1px solid rgba(255, 191, 118, 0.24); color: #ffd69b; }
.admin-row-actions { padding-top: 4px; }
.admin-money-stack { display: grid; gap: 4px; align-content: start; }
.admin-money-stack strong { font-size: 15px; }
.admin-money-stack span, .admin-money-stack small { color: rgba(208,219,238,0.78); }
.admin-grid { grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); }
.admin-card h3 { margin: 0; }
.admin-meta span { display: inline-flex; align-items: center; gap: 8px; }
.admin-report-grid { grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr); }
.admin-report-tables { display: grid; gap: 14px; }
.admin-table td { vertical-align: top; }
.admin-table--ledger td:nth-child(2) { white-space: nowrap; }
.admin-chart { display: grid; grid-template-columns: repeat(auto-fit, minmax(72px, 1fr)); gap: 10px; }
.admin-chart__item { display: grid; gap: 10px; text-align: center; }
.admin-chart__bars { height: 210px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; align-items: end; padding: 10px; border-radius: 18px; background: rgba(255,255,255,0.03); border: 1px solid rgba(186,204,255,0.08); }
.admin-chart__bar { width: 100%; min-height: 6px; border-radius: 14px 14px 6px 6px; }
.admin-chart__bar--sales { background: linear-gradient(180deg, #8dd0ff, #3f83f8); }
.admin-chart__bar--net { background: linear-gradient(180deg, #7bf1c8, #16a34a); }
.admin-top__item { display: grid; gap: 10px; grid-template-columns: minmax(0, 1fr) auto auto; align-items: center; }
@media (max-width: 1180px) { .admin-hero-stats, .admin-nav, .admin-kpis--four, .admin-report-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 920px) { .admin-hero-stats, .admin-nav, .admin-kpis, .admin-kpis--four, .admin-filters, .admin-report-filters, .admin-report-grid, .admin-filters--ledger { grid-template-columns: 1fr; } .admin-search-cluster { grid-template-columns: 1fr; } .admin-nav__card { grid-template-columns: auto 1fr; } .admin-nav__card span:last-child { grid-column: 1 / -1; justify-self: start; } .admin-top__item { grid-template-columns: 1fr; } }
</style>
